/**
 * Diagram validation utilities
 *
 * Validates diagram state against entity-specific rules before saving.
 */

import { DiagramState } from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";
import { conditionFormSchema } from "components/CC/Edges/ConditionEdge/Forms/formSchema";
import { breakerFormSchema } from "components/CC/Nodes/BreakerNode/Forms/formSchema";
import { httpFormSchema } from "components/CC/Nodes/HttpNode/Forms/formSchema";
import { modifyFieldFormSchema } from "components/CC/Nodes/ModifyFieldNode/Forms/formSchema";
import { repeaterFormSchema } from "components/CC/Nodes/RepeaterNode/Forms/formSchema";
import { setFormSchema } from "components/CC/Nodes/SetNode/Forms/formSchema";
import { waitFormSchema } from "components/CC/Nodes/WaitNode/Forms/formSchema";
import { webhookFormSchema } from "components/CC/Nodes/WebhookNode/Forms/formSchema";
import { CC_ENTITY_CONFIGS, CCEntityType } from "components/CC/types/entityConfig";
import { NodeType, StartFlowType } from "components/CC/types/enums";
import { DrawerId } from "components/CC/types/models";

import { getDefaultFormData } from "./nodeDefaults";

import type { ZodTypeAny } from "zod";

export interface ValidationError {
    key?: string;
    messageKey?: string;
    params?: Record<string, string | number>;
    label?: string;
    labelKey?: string;
    /** Node ID for linking to drawer */
    nodeId?: string;
    /** Edge ID for linking to drawer */
    edgeId?: string;
    /** Drawer ID to open for fixing the error */
    drawerId?: DrawerId;
}

/**
 * Node type to drawer ID mapping
 * Note: Methods node is a FE-only placeholder node without a drawer
 */
const NODE_TYPE_TO_DRAWER: Partial<Record<string, DrawerId>> = {
    [NodeType.Http]: DrawerId.Http,
    [NodeType.Start]: DrawerId.Start,
    [NodeType.End]: DrawerId.End,
    [NodeType.Wait]: DrawerId.Wait,
    [NodeType.Set]: DrawerId.Set,
    [NodeType.Modify]: DrawerId.ModifyField,
    [NodeType.Repeater]: DrawerId.Repeater,
    [NodeType.Mirror]: DrawerId.Mirror,
    [NodeType.SubScenario]: DrawerId.SubScenario,
    [NodeType.Webhook]: DrawerId.Webhook,
    [NodeType.Breaker]: DrawerId.Breaker,
    // Methods node is FE-only placeholder, no drawer needed
};

const NODE_FORM_SCHEMAS: Partial<Record<string, ZodTypeAny>> = {
    // SubScenario and Mirror omitted - their forms have no required fields currently.
    [NodeType.Http]: httpFormSchema,
    [NodeType.Webhook]: webhookFormSchema,
    [NodeType.Wait]: waitFormSchema,
    [NodeType.Breaker]: breakerFormSchema,
    [NodeType.Set]: setFormSchema,
    [NodeType.Modify]: modifyFieldFormSchema,
    [NodeType.Repeater]: repeaterFormSchema,
};

const NODE_TYPE_LABEL_KEYS: Partial<Record<NodeType, string>> = {
    [NodeType.Wait]: "components.connection-creator.nodes.wait.title",
    [NodeType.Breaker]: "components.connection-creator.nodes.breaker.title",
    [NodeType.Set]: "components.connection-creator.nodes.set.title",
    [NodeType.Modify]: "components.connection-creator.nodes.modify.title",
    [NodeType.Http]: "components.connection-creator.nodes.http.title",
    [NodeType.Webhook]: "components.connection-creator.nodes.webhook.title",
};

const CONDITION_EDGE_VALIDATION_DEFAULTS = {
    active: true,
    title: "",
    subtitle: "",
    result: "success",
    // Intentionally differs from conditionFormDefaultValues.
    // Diagram-level validation should not fail older/imported edges that have no conditions array yet;
    // the drawer form still seeds one empty row for editing UX.
    conditions: [],
};

// Existing diagrams use "customEdge" for condition-configurable edges.
// Keep "conditionEdge" for drawer/blueprint compatibility and ignore any future edge types by default.
const CONDITION_EDGE_TYPES = new Set(["customEdge", "conditionEdge"]);

function getCustomLabel(title: unknown): string | undefined {
    return typeof title === "string" && title.trim() !== "" ? title.trim() : undefined;
}

function buildNodeValidationError(
    nodeType: string,
    nodeId: string,
    formData: Record<string, unknown> | undefined
): ValidationError | null {
    const schema = NODE_FORM_SCHEMAS[nodeType];
    const drawerId = NODE_TYPE_TO_DRAWER[nodeType];

    if (!schema || !drawerId) {
        return null;
    }

    const mergedFormData = {
        ...(getDefaultFormData(nodeType as NodeType) as Record<string, unknown>),
        ...(formData || {}),
    };
    const result = schema.safeParse(mergedFormData);

    if (result.success || result.error.issues.length === 0) {
        return null;
    }

    return {
        // Save-time modal shows one actionable issue per node to avoid flooding the user with repeated row errors.
        messageKey: result.error.issues[0].message,
        label: getCustomLabel(formData?.title),
        labelKey: NODE_TYPE_LABEL_KEYS[nodeType as NodeType],
        nodeId,
        drawerId,
    };
}

function buildEdgeValidationError(
    edgeId: string,
    edgeData: Record<string, unknown> | undefined
): ValidationError | null {
    const mergedEdgeData = {
        ...CONDITION_EDGE_VALIDATION_DEFAULTS,
        ...(edgeData || {}),
    };
    const result = conditionFormSchema.safeParse(mergedEdgeData);

    if (result.success || result.error.issues.length === 0) {
        return null;
    }

    return {
        // Save-time modal shows one actionable issue per edge to keep the error list short and clickable.
        messageKey: result.error.issues[0].message,
        label: getCustomLabel(edgeData?.title),
        labelKey: "components.connection-creator.edges.condition.drawers.title",
        edgeId,
        drawerId: DrawerId.Condition,
    };
}

/**
 * Validate diagram state against entity rules.
 * Returns an array of validation errors (empty if valid).
 */
export const validateDiagram = (state: DiagramState, entityType: CCEntityType): ValidationError[] => {
    const errors: ValidationError[] = [];
    const config = CC_ENTITY_CONFIGS[entityType];

    // Find all start nodes
    const startNodes = state.nodes.filter((n) => n.type === "startNode");

    // Check max starts
    if (startNodes.length > config.maxStarts) {
        errors.push({
            key: "too_many_starts",
            params: {
                max: config.maxStarts,
                count: startNodes.length,
            },
        });
    }

    // Check flow types
    const invalidFlows: string[] = [];
    startNodes.forEach((node) => {
        const formData = node.data?.formData as { flow?: string } | undefined;
        const flow = formData?.flow as StartFlowType | undefined;
        if (flow && !config.allowedFlowTypes.includes(flow)) {
            invalidFlows.push(flow);
        }
    });

    if (invalidFlows.length > 0) {
        const uniqueInvalidFlows = [...new Set(invalidFlows)];
        errors.push({
            key: "invalid_flow_types",
            params: {
                invalid: uniqueInvalidFlows.join(", "),
                allowed: config.allowedFlowTypes.join(", "),
            },
        });
    }

    // Check unique flow types if enforced
    if (config.enforceUniqueFlowTypes && startNodes.length > 1) {
        const flows = startNodes.map((n) => (n.data?.formData as { flow?: string } | undefined)?.flow).filter(Boolean);
        const uniqueFlows = new Set(flows);
        if (uniqueFlows.size !== flows.length) {
            errors.push({
                key: "duplicate_flow_types",
            });
        }
    }

    // Validate node-specific required fields
    // Skip FE-only placeholder nodes (e.g., Methods) that have no drawer
    const FE_ONLY_NODE_TYPES = [NodeType.Methods];

    state.nodes.forEach((node) => {
        const nodeType = node.type;
        if (!nodeType) return;

        // Skip FE-only nodes - they don't have validation rules or drawers
        if (FE_ONLY_NODE_TYPES.includes(nodeType as NodeType)) return;

        const formData = node.data?.formData as Record<string, unknown> | undefined;
        const nodeError = buildNodeValidationError(nodeType, node.id, formData);
        if (nodeError) {
            errors.push(nodeError);
        }
    });

    state.edges.forEach((edge) => {
        if (!edge.type || !CONDITION_EDGE_TYPES.has(edge.type)) {
            return;
        }

        const edgeData = edge.data as Record<string, unknown> | undefined;
        const edgeError = buildEdgeValidationError(edge.id, edgeData);
        if (edgeError) {
            errors.push(edgeError);
        }
    });

    return errors;
};
